import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AxiosError } from 'axios';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLogin } from '@/shared/hooks';
import {
  Alert,
  AlertDescription,
  Button,
  Input,
  Label,
} from '@/shared/components/ui';
import { Eye, EyeOff, AlertCircle } from 'lucide-react';
import mamLogo from '@/assets/mamLogo.svg';
import { AUTH_STRINGS } from '@/shared/constants/strings';
import { LoginFormData, loginSchema } from '@/shared/schemas';

// ─── Type Guards ──────────────────────────────────────────────────────────────

/**
 * Type guard to safely check if an error is an AxiosError with a response.
 * Validates that error has the expected structure before accessing properties.
 */
function isAxiosErrorWithResponse(
  error: unknown
): error is AxiosError<unknown> & { response: { status: number } } {
  if (error === null || typeof error !== 'object') return false;

  const err = error as Record<string, unknown>;

  if (!('response' in err)) return false;
  if (err.response === null || typeof err.response !== 'object') return false;

  const response = err.response as Record<string, unknown>;

  return 'status' in response && typeof response.status === 'number';
}

export default function Login() {
  const [showPassword, setShowPassword] = useState(false);
  const location = useLocation();

  // Initialize lockout alert state based on location.state
  const lockoutErrorFromState = (
    location.state as { lockoutError?: boolean } | null
  )?.lockoutError;
  const [showLockoutAlert, setShowLockoutAlert] = useState(
    !!lockoutErrorFromState
  );

  const loginMutation = useLogin();

  const isAccountLocked =
    loginMutation.isError &&
    isAxiosErrorWithResponse(loginMutation.error) &&
    loginMutation.error.response.status === 429;

  // Clear the state so it doesn't persist on navigation
  useEffect(() => {
    if (lockoutErrorFromState) {
      globalThis.history.replaceState({}, document.title, location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    mode: 'onSubmit',
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = (data: LoginFormData) => {
    loginMutation.mutate(data);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      {/* Centered White Card */}
      <div className="w-full max-w-xl bg-white rounded-2xl shadow-2xl p-5 sm:p-8">
        {/* Header inside card */}
        <div className="flex justify-center mb-8 sm:mb-12">
          <img
            src={mamLogo}
            alt="MamBaby Logo"
            className="w-12 h-12"
            width="48"
            height="48"
            fetchPriority="high"
          />
        </div>

        {/* Title */}
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground mb-3">
            {AUTH_STRINGS.LOGIN.TITLE}
          </h1>
          <p className="text-muted-foreground text-base">
            {AUTH_STRINGS.LOGIN.SUBTITLE}
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          {/* MFA Lockout Alert (from location.state after too many OTP failures) */}
          {showLockoutAlert && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="flex justify-between items-center">
                <span>{AUTH_STRINGS.LOGIN.LOCKOUT_ERROR}</span>
                <button
                  type="button"
                  onClick={() => setShowLockoutAlert(false)}
                  className="ml-4 text-sm hover:underline"
                >
                  {AUTH_STRINGS.LOGIN.LOCKOUT_ALERT_DISMISS}
                </button>
              </AlertDescription>
            </Alert>
          )}

          {/* Login Lockout Alert (429 — account locked after too many failed login attempts) */}
          {isAccountLocked && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {loginMutation.error?.message ||
                  AUTH_STRINGS.LOGIN.LOGIN_LOCKOUT_ERROR}
              </AlertDescription>
            </Alert>
          )}

          {/* Generic Login Error Alert (non-429 errors only) */}
          {loginMutation.isError && !isAccountLocked && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {loginMutation.error?.message ||
                  AUTH_STRINGS.LOGIN.ERROR_MESSAGE}
              </AlertDescription>
            </Alert>
          )}

          {/* Email Field */}
          <div className="space-y-2">
            <Label
              htmlFor="email"
              className="text-foreground text-sm font-medium"
            >
              {AUTH_STRINGS.LOGIN.EMAIL_LABEL}
            </Label>
            <Input
              id="email"
              type="email"
              placeholder={AUTH_STRINGS.LOGIN.EMAIL_PLACEHOLDER}
              {...register('email')}
              className="h-12 bg-input-muted border-0 text-foreground placeholder:text-muted-foreground focus-visible:ring-2"
              disabled={loginMutation.isPending}
            />
            {errors.email && (
              <p className="text-sm text-destructive">{errors.email.message}</p>
            )}
          </div>

          {/* Password Field */}
          <div className="space-y-2">
            <Label
              htmlFor="password"
              className="text-foreground text-sm font-medium"
            >
              {AUTH_STRINGS.LOGIN.PASSWORD_LABEL}
            </Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder={AUTH_STRINGS.LOGIN.PASSWORD_PLACEHOLDER}
                {...register('password')}
                className="h-12 bg-input-muted border-0 text-foreground pr-12 focus-visible:ring-2"
                disabled={loginMutation.isPending}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground/70 hover:text-muted-foreground"
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>
            {errors.password && (
              <p className="text-sm text-destructive">
                {errors.password.message}
              </p>
            )}
          </div>

          {/* Forgot Password Link */}
          <div className="text-right -mt-2">
            <Link
              to="/auth/forgot-password"
              className="text-sm font-medium text-brand-blue hover:underline"
            >
              {AUTH_STRINGS.LOGIN.FORGOT_PASSWORD}
            </Link>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            className="w-full h-12 text-white text-base font-medium rounded-lg mt-6 bg-brand-blue hover:bg-primary-hover"
            disabled={loginMutation.isPending}
          >
            {loginMutation.isPending ? (
              <span className="flex items-center justify-center">
                <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                {AUTH_STRINGS.LOGIN.LOADING_TEXT}
              </span>
            ) : (
              AUTH_STRINGS.LOGIN.SUBMIT_BUTTON
            )}
          </Button>

          {/* Sign Up Link */}
          <div className="text-center text-muted-foreground text-sm pt-4">
            {AUTH_STRINGS.LOGIN.NO_ACCOUNT}{' '}
            <Link
              to="/register"
              className="font-semibold hover:underline text-brand-blue"
            >
              {AUTH_STRINGS.LOGIN.SIGN_UP_LINK}
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
